// src/pages/CertificadosPage.js — Perfil de usuario con sidebar (5 secciones)
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { useIdioma } from '../contexts/IdiomaContext';
import {
  certificadoService, profileService, miRankingService,
  userService, convenioService, especialidadService, paisService,
  BACKEND_BASE
} from '../services/api';
import { downloadBlob } from '../utils/downloadHelper';
import { toast } from 'react-toastify';

const COLORS = {
  primary: '#0f355b',
  accent: '#14b6cb',
  danger: '#dc2626',
  success: '#059669',
  text: '#374151',
  textLight: '#6b7280',
  textMuted: '#9ca3af',
  border: '#e5e7eb',
  bg: '#f8fafc',
  white: '#ffffff',
};

// ============================================================
// STYLED COMPONENTS
// ============================================================

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem;
  font-family: 'Segoe UI', sans-serif;
`;

const ProfileHeader = styled.div`
  background: linear-gradient(135deg, ${COLORS.primary} 0%, #1a4a7a 100%);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(15, 53, 91, 0.25);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;
  }
`;

const AvatarLarge = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #14b6cb, #22d3ee);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 12px rgba(20, 182, 203, 0.4);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.4rem;
  font-weight: 700;
`;

const ProfileEmail = styled.p`
  margin: 0;
  opacity: 0.8;
  font-size: 0.9rem;
`;

const ProfileBadge = styled.span`
  display: inline-block;
  margin-top: 0.5rem;
  padding: 0.2rem 0.75rem;
  background: ${p => p.$accent ? 'rgba(20,182,203,0.25)' : 'rgba(255,255,255,0.15)'};
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
  margin-right: 0.5rem;
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Sidebar = styled.nav`
  background: ${COLORS.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    border-radius: 10px;
    &::-webkit-scrollbar { height: 0; }
  }
`;

const SidebarItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.85rem 1.1rem;
  border: none;
  background: ${p => p.$active ? `${COLORS.primary}0d` : 'transparent'};
  color: ${p => p.$active ? COLORS.primary : COLORS.textLight};
  font-weight: ${p => p.$active ? '700' : '500'};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.15s;
  border-left: 3px solid ${p => p.$active ? COLORS.accent : 'transparent'};
  white-space: nowrap;

  &:hover { background: ${COLORS.primary}08; color: ${COLORS.primary}; }

  @media (max-width: 768px) {
    width: auto;
    border-left: none;
    border-bottom: 3px solid ${p => p.$active ? COLORS.accent : 'transparent'};
    padding: 0.75rem 1rem;
    flex-shrink: 0;
  }
`;

const ContentArea = styled.div`
  background: ${COLORS.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 1.5rem;
  min-height: 300px;
`;

const SectionTitle = styled.h3`
  color: ${COLORS.primary};
  font-size: 1.15rem;
  margin: 0 0 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Certificate styles
const CertList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.25rem;
`;

const CertCard = styled.div`
  background: ${COLORS.bg};
  border-radius: 10px;
  padding: 1.25rem;
  text-align: center;
  border: 1px solid ${COLORS.border};
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
`;

const CertLogo = styled.img`
  width: 48px; height: 48px; object-fit: contain; margin-bottom: 0.5rem;
`;

const CertTitle = styled.h4`
  color: ${COLORS.primary}; font-size: 0.95rem; margin: 0 0 0.3rem;
`;

const CertMeta = styled.p`
  color: ${COLORS.textLight}; font-size: 0.82rem; margin: 0.15rem 0;
`;

const CertCode = styled.div`
  font-family: monospace;
  background: #f0fdf4;
  color: #065f46;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  display: inline-block;
  margin: 0.4rem 0;
  font-size: 0.82rem;
  letter-spacing: 1px;
`;

const Btn = styled.button`
  background: ${p => p.$danger ? COLORS.danger : p.$ghost ? 'transparent' : `linear-gradient(135deg, ${COLORS.primary}, #1a4a7a)`};
  color: ${p => p.$ghost ? COLORS.text : 'white'};
  border: ${p => p.$ghost ? `1px solid ${COLORS.border}` : 'none'};
  padding: 0.5rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem 1rem;
  color: ${COLORS.textMuted};
  font-size: 0.95rem;
  background: ${COLORS.bg};
  border-radius: 10px;
  border: 1px dashed ${COLORS.border};
`;

// Form styles
const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${COLORS.text};
  margin-bottom: 0.3rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  font-size: 0.9rem;
  box-sizing: border-box;
  transition: border 0.2s;
  &:focus { outline: none; border-color: ${COLORS.accent}; }
  &:read-only { background: ${COLORS.bg}; color: ${COLORS.textLight}; }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  font-size: 0.9rem;
  box-sizing: border-box;
  background: white;
  &:focus { outline: none; border-color: ${COLORS.accent}; }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Msg = styled.div`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 1rem;
  background: ${p => p.$error ? '#fef2f2' : '#f0fdf4'};
  color: ${p => p.$error ? '#991b1b' : '#065f46'};
  border: 1px solid ${p => p.$error ? '#fecaca' : '#bbf7d0'};
`;

// Ranking styles
const RankCard = styled.div`
  background: ${COLORS.bg};
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid ${COLORS.border};
`;

const RankPosition = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px; height: 32px;
  border-radius: 50%;
  font-weight: 800;
  font-size: 0.9rem;
  margin-right: 0.75rem;
  background: ${p =>
    p.$pos === 1 ? 'linear-gradient(135deg, #f59e0b, #eab308)' :
    p.$pos === 2 ? 'linear-gradient(135deg, #94a3b8, #cbd5e1)' :
    p.$pos === 3 ? 'linear-gradient(135deg, #b45309, #d97706)' :
    COLORS.primary};
  color: white;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: ${COLORS.border};
  border-radius: 4px;
  margin: 0.5rem 0;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${COLORS.accent}, ${COLORS.primary});
  border-radius: 4px;
  width: ${p => p.$pct}%;
  transition: width 0.5s;
`;

const StatGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 1rem; margin-top: 0.75rem;
`;

const StatItem = styled.div`
  font-size: 0.82rem; color: ${COLORS.textLight};
  span { font-weight: 700; color: ${COLORS.text}; }
`;

// Avatar section
const AvatarEditWrap = styled.div`
  display: flex; align-items: center; gap: 1.5rem; margin-bottom: 1.5rem;
  @media (max-width: 600px) { flex-direction: column; }
`;

const AvatarPreview = styled.div`
  width: 100px; height: 100px; border-radius: 50%;
  background: linear-gradient(135deg, ${COLORS.accent}, #22d3ee);
  display: flex; align-items: center; justify-content: center;
  font-size: 2.5rem; font-weight: 700; color: white;
  overflow: hidden; flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(20,182,203,0.3);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const LangPills = styled.div`
  display: flex; gap: 4px; margin-top: 0.5rem;
`;

const LangPill = styled.button`
  padding: 0.4rem 1rem;
  border: 2px solid ${p => p.$active ? COLORS.accent : COLORS.border};
  border-radius: 8px;
  background: ${p => p.$active ? `${COLORS.accent}15` : 'white'};
  color: ${p => p.$active ? COLORS.primary : COLORS.textLight};
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: ${COLORS.accent}; }
`;

// ============================================================
// TABS CONFIG
// ============================================================
const TABS = [
  { id: 'certificados', icon: '\uD83D\uDCDC', label: 'Certificados' },
  { id: 'datos', icon: '\uD83D\uDCDD', label: 'Mis Datos' },
  { id: 'ranking', icon: '\uD83C\uDFC6', label: 'Mi Ranking' },
  { id: 'seguridad', icon: '\uD83D\uDD12', label: 'Seguridad' },
  { id: 'personalizacion', icon: '\u2699\uFE0F', label: 'Personalización' },
];

// ============================================================
// MAIN COMPONENT
// ============================================================
const CertificadosPage = () => {
  const { user, updateUser } = useAuth();
  const { proyecto } = useProject();
  const { setIdioma } = useIdioma();
  const [activeTab, setActiveTab] = useState('certificados');

  // Profile data
  const [profile, setProfile] = useState(null);
  const [registroConfig, setRegistroConfig] = useState(null);
  const [convenios, setConvenios] = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);

  // Load profile once
  useEffect(() => {
    if (!user) return;
    profileService.getProfile()
      .then(res => {
        setProfile(res.data.user);
        setRegistroConfig(res.data.registro_config);
        setConvenios(res.data.convenios || []);
      })
      .catch(e => console.error('Error loading profile:', e))
      .finally(() => setProfileLoading(false));
  }, [user]);

  const getInitials = () => {
    const name = user?.nombre || user?.email || '?';
    return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  };

  const avatarUrl = profile?.avatar_url || user?.avatar_url;

  if (!user) return null;

  return (
    <Page>
      {/* Profile Header */}
      <ProfileHeader>
        <AvatarLarge>
          {avatarUrl
            ? <img src={`${BACKEND_BASE}/${avatarUrl}`} alt="Avatar" />
            : getInitials()
          }
        </AvatarLarge>
        <ProfileInfo>
          <ProfileName>{user.nombre || 'Usuario'}</ProfileName>
          <ProfileEmail>{user.email}</ProfileEmail>
          {user.role && <ProfileBadge>{user.role}</ProfileBadge>}
          {proyecto?.nombre && <ProfileBadge $accent>{proyecto.nombre}</ProfileBadge>}
        </ProfileInfo>
      </ProfileHeader>

      {/* Layout: Sidebar + Content */}
      <LayoutGrid>
        <Sidebar>
          {TABS.map(tab => (
            <SidebarItem
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.icon}</span> {tab.label}
            </SidebarItem>
          ))}
        </Sidebar>

        <ContentArea>
          {activeTab === 'certificados' && <CertificadosSection />}
          {activeTab === 'datos' && (
            <DatosSection
              profile={profile}
              setProfile={setProfile}
              registroConfig={registroConfig}
              convenios={convenios}
              loading={profileLoading}
              updateUser={updateUser}
            />
          )}
          {activeTab === 'ranking' && <RankingSection />}
          {activeTab === 'seguridad' && <SeguridadSection />}
          {activeTab === 'personalizacion' && (
            <PersonalizacionSection
              profile={profile}
              setProfile={setProfile}
              updateUser={updateUser}
              setIdioma={setIdioma}
            />
          )}
        </ContentArea>
      </LayoutGrid>
    </Page>
  );
};

// ============================================================
// 1. CERTIFICADOS SECTION
// ============================================================
const CertificadosSection = () => {
  const { user } = useAuth();
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    certificadoService.misCertificados()
      .then(res => setCertificados(res.data?.certificados || []))
      .catch(e => console.error('Error cargando certificados:', e))
      .finally(() => setLoading(false));
  }, []);

  const handleDescargar = async (certId) => {
    setDownloading(certId);
    try {
      const res = await certificadoService.descargarPDF(certId);
      downloadBlob(res.data, `certificado_${certId}.pdf`, 'application/pdf');
    } catch (e) {
      console.error('Error descargando:', e);
      toast.error('Error al descargar el certificado');
    }
    setDownloading(null);
  };

  return (
    <>
      <SectionTitle>Mis Certificados</SectionTitle>
      {loading ? (
        <p style={{ color: COLORS.textLight, textAlign: 'center', padding: '2rem' }}>Cargando...</p>
      ) : certificados.length > 0 ? (
        <CertList>
          {certificados.map(cert => (
            <CertCard key={cert.id}>
              {cert.proyecto_logo && (
                <CertLogo src={`${BACKEND_BASE}/${cert.proyecto_logo}`} alt="" />
              )}
              <CertTitle>{cert.proyecto_nombre}</CertTitle>
              <CertMeta>Emitido: {new Date(cert.fecha_emision).toLocaleDateString('es-ES')}</CertMeta>
              <CertCode>{cert.codigo_verificacion}</CertCode>
              <br />
              <Btn onClick={() => handleDescargar(cert.id)} disabled={downloading === cert.id}>
                {downloading === cert.id ? 'Descargando...' : 'Descargar PDF'}
              </Btn>
            </CertCard>
          ))}
        </CertList>
      ) : (
        <EmptyState>
          No tienes certificados aun. Completa los requisitos de un proyecto para obtener tu certificado.
        </EmptyState>
      )}
    </>
  );
};

// ============================================================
// 2. DATOS SECTION
// ============================================================
const DatosSection = ({ profile, setProfile, registroConfig, convenios, loading, updateUser }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [especialidades, setEspecialidades] = useState([]);
  const [paises, setPaises] = useState([]);

  useEffect(() => {
    if (profile) {
      setForm({
        nombre: profile.nombre || '',
        telefono: profile.telefono || '',
        codigo_pais: profile.codigo_pais || '+57',
        convenio_id: profile.convenio_id || '',
        especialidad: profile.especialidad || '',
        pais: profile.pais || '',
        dni: profile.dni || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    especialidadService.list().then(r => setEspecialidades(r.data?.especialidades || r.data || [])).catch(() => {});
    paisService.list().then(r => setPaises(r.data?.paises || r.data || [])).catch(() => {});
  }, []);

  const campos = registroConfig?.campos || {};
  const isVisible = (key) => campos[key]?.visible !== false;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await profileService.updateProfile(form);
      if (res.data?.user) {
        setProfile(prev => ({ ...prev, ...res.data.user }));
        updateUser({ nombre: form.nombre });
      }
      toast.success('Datos actualizados correctamente');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al guardar');
    }
    setSaving(false);
  };

  if (loading || !profile) return <p style={{ color: COLORS.textLight }}>Cargando datos...</p>;

  return (
    <>
      <SectionTitle>Mis Datos</SectionTitle>
      <FormRow>
        <FormGroup>
          <Label>Nombre</Label>
          <Input value={form.nombre || ''} onChange={e => setForm({...form, nombre: e.target.value})} />
        </FormGroup>
        <FormGroup>
          <Label>Email</Label>
          <Input value={profile.email || ''} readOnly />
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label>Rol</Label>
          <Input value={profile.role || ''} readOnly />
        </FormGroup>
        {isVisible('telefono') && (
          <FormGroup>
            <Label>Teléfono</Label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Input
                style={{ width: '80px', flexShrink: 0 }}
                value={form.codigo_pais || ''}
                onChange={e => setForm({...form, codigo_pais: e.target.value})}
                placeholder="+57"
              />
              <Input
                value={form.telefono || ''}
                onChange={e => setForm({...form, telefono: e.target.value})}
                placeholder="Teléfono"
              />
            </div>
          </FormGroup>
        )}
      </FormRow>

      {isVisible('convenio') && convenios.length > 0 && (
        <FormGroup>
          <Label>Convenio / Institución</Label>
          <Select value={form.convenio_id || ''} onChange={e => setForm({...form, convenio_id: e.target.value})}>
            <option value="">— Sin convenio —</option>
            {convenios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
        </FormGroup>
      )}

      <FormRow>
        {isVisible('especialidad') && (
          <FormGroup>
            <Label>Especialidad</Label>
            {especialidades.length > 0 ? (
              <Select value={form.especialidad || ''} onChange={e => setForm({...form, especialidad: e.target.value})}>
                <option value="">— Seleccionar —</option>
                {especialidades.map((e, i) => <option key={i} value={typeof e === 'string' ? e : e.nombre}>{typeof e === 'string' ? e : e.nombre}</option>)}
              </Select>
            ) : (
              <Input value={form.especialidad || ''} onChange={e => setForm({...form, especialidad: e.target.value})} />
            )}
          </FormGroup>
        )}
        {isVisible('pais') && (
          <FormGroup>
            <Label>País</Label>
            {paises.length > 0 ? (
              <Select value={form.pais || ''} onChange={e => setForm({...form, pais: e.target.value})}>
                <option value="">— Seleccionar —</option>
                {paises.map((p, i) => <option key={i} value={typeof p === 'string' ? p : p.nombre}>{typeof p === 'string' ? p : p.nombre}</option>)}
              </Select>
            ) : (
              <Input value={form.pais || ''} onChange={e => setForm({...form, pais: e.target.value})} />
            )}
          </FormGroup>
        )}
      </FormRow>

      {isVisible('dni') && (
        <FormGroup>
          <Label>Documento de identidad (DNI)</Label>
          <Input value={form.dni || ''} onChange={e => setForm({...form, dni: e.target.value})} />
        </FormGroup>
      )}

      <div style={{ marginTop: '1rem' }}>
        <Btn onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Btn>
      </div>
    </>
  );
};

// ============================================================
// 3. RANKING SECTION
// ============================================================
const RankingSection = () => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    miRankingService.getRankingGlobal()
      .then(res => setRankings(res.data?.rankings || []))
      .catch(e => console.error('Error ranking:', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: COLORS.textLight, textAlign: 'center', padding: '2rem' }}>Cargando ranking...</p>;

  return (
    <>
      <SectionTitle>Mi Ranking</SectionTitle>
      {rankings.length > 0 ? rankings.map((r, i) => (
        <RankCard key={i}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <RankPosition $pos={r.posicion}>#{r.posicion}</RankPosition>
            <div>
              <strong style={{ color: COLORS.primary }}>{r.documento_titulo}</strong>
              <div style={{ fontSize: '0.82rem', color: COLORS.textLight }}>
                Puesto #{r.posicion} de {r.total_participantes} participantes
              </div>
            </div>
          </div>
          <ProgressBar>
            <ProgressFill $pct={r.percentil} />
          </ProgressBar>
          <div style={{ fontSize: '0.78rem', color: COLORS.textMuted, textAlign: 'right' }}>
            Percentil: {r.percentil}%
          </div>
          <StatGrid>
            <StatItem><span>{r.stats.sesiones}</span> sesiones</StatItem>
            <StatItem><span>{r.stats.preguntas}</span> preguntas</StatItem>
            <StatItem><span>{r.stats.tiempo_total}</span> de estudio</StatItem>
            {r.stats.promedio_evaluacion > 0 && (
              <StatItem>Promedio: <span>{r.stats.promedio_evaluacion}%</span></StatItem>
            )}
            {r.stats.evaluaciones_aprobadas > 0 && (
              <StatItem><span>{r.stats.evaluaciones_aprobadas}</span> evaluaciones aprobadas</StatItem>
            )}
          </StatGrid>
        </RankCard>
      )) : (
        <EmptyState>
          Aún no tienes actividad registrada. Comienza a estudiar para ver tu ranking.
        </EmptyState>
      )}
    </>
  );
};

// ============================================================
// 4. SEGURIDAD SECTION
// ============================================================
const SeguridadSection = () => {
  const [form, setForm] = useState({ actual: '', nueva: '', confirmar: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSubmit = async () => {
    setMsg(null);
    if (form.nueva.length < 6) {
      setMsg({ error: true, text: 'La nueva contraseña debe tener al menos 6 caracteres' });
      return;
    }
    if (form.nueva !== form.confirmar) {
      setMsg({ error: true, text: 'Las contraseñas no coinciden' });
      return;
    }
    setSaving(true);
    try {
      await profileService.changePassword(form.actual, form.nueva);
      setMsg({ error: false, text: 'Contraseña actualizada correctamente' });
      setForm({ actual: '', nueva: '', confirmar: '' });
    } catch (e) {
      setMsg({ error: true, text: e.response?.data?.message || 'Error al cambiar la contraseña' });
    }
    setSaving(false);
  };

  return (
    <>
      <SectionTitle>Seguridad</SectionTitle>
      {msg && <Msg $error={msg.error}>{msg.text}</Msg>}
      <FormGroup>
        <Label>Contraseña actual</Label>
        <Input type="password" value={form.actual} onChange={e => setForm({...form, actual: e.target.value})} />
      </FormGroup>
      <FormRow>
        <FormGroup>
          <Label>Nueva contraseña</Label>
          <Input type="password" value={form.nueva} onChange={e => setForm({...form, nueva: e.target.value})} />
        </FormGroup>
        <FormGroup>
          <Label>Confirmar nueva</Label>
          <Input type="password" value={form.confirmar} onChange={e => setForm({...form, confirmar: e.target.value})} />
        </FormGroup>
      </FormRow>
      <Btn onClick={handleSubmit} disabled={saving || !form.actual || !form.nueva}>
        {saving ? 'Cambiando...' : 'Cambiar Contraseña'}
      </Btn>

      <div style={{ marginTop: '2rem', padding: '1rem', background: COLORS.bg, borderRadius: '8px', border: `1px solid ${COLORS.border}` }}>
        <div style={{ color: COLORS.textMuted, fontSize: '0.85rem' }}>
          <strong>Autenticación de dos factores (2FA)</strong>
          <p style={{ margin: '0.3rem 0 0' }}>Próximamente disponible.</p>
        </div>
      </div>
    </>
  );
};

// ============================================================
// 5. PERSONALIZACION SECTION
// ============================================================
const PersonalizacionSection = ({ profile, setProfile, updateUser, setIdioma }) => {
  const { user } = useAuth();
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [currentIdioma, setCurrentIdioma] = useState(user?.idioma_preferido || 'es');

  const currentAvatarUrl = profile?.avatar_url || user?.avatar_url;

  const getInitials = () => {
    const name = user?.nombre || user?.email || '?';
    return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo excede 2MB');
      return;
    }
    setAvatarFile(file);
    setAvatarPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!avatarFile) return;
    setUploading(true);
    try {
      const res = await profileService.uploadAvatar(avatarFile);
      const newUrl = res.data.avatar_url;
      setProfile(prev => ({ ...prev, avatar_url: newUrl }));
      updateUser({ avatar_url: newUrl });
      setAvatarFile(null);
      setAvatarPreviewUrl(null);
      toast.success('Avatar actualizado');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al subir avatar');
    }
    setUploading(false);
  };

  const handleIdiomaChange = async (lang) => {
    setCurrentIdioma(lang);
    try {
      await userService.updateIdioma(lang);
      setIdioma(lang);
      updateUser({ idioma_preferido: lang });
      toast.success('Idioma actualizado');
    } catch (e) {
      toast.error('Error al cambiar idioma');
    }
  };

  return (
    <>
      <SectionTitle>Personalización</SectionTitle>

      {/* Avatar */}
      <Label style={{ marginBottom: '0.75rem' }}>Foto de perfil</Label>
      <AvatarEditWrap>
        <AvatarPreview>
          {avatarPreviewUrl
            ? <img src={avatarPreviewUrl} alt="Preview" />
            : currentAvatarUrl
              ? <img src={`${BACKEND_BASE}/${currentAvatarUrl}`} alt="Avatar" />
              : getInitials()
          }
        </AvatarPreview>
        <div>
          <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileChange} />
          <p style={{ fontSize: '0.8rem', color: COLORS.textMuted, margin: '0.3rem 0' }}>
            JPEG, PNG, GIF o WebP. Máximo 2MB.
          </p>
          {avatarFile && (
            <Btn onClick={handleUpload} disabled={uploading} style={{ marginTop: '0.5rem' }}>
              {uploading ? 'Subiendo...' : 'Guardar Avatar'}
            </Btn>
          )}
        </div>
      </AvatarEditWrap>

      {/* Idioma */}
      <Label>Idioma de la interfaz</Label>
      <LangPills>
        {[
          { code: 'es', label: 'Español' },
          { code: 'en', label: 'English' },
          { code: 'pt', label: 'Português' },
        ].map(l => (
          <LangPill
            key={l.code}
            $active={currentIdioma === l.code}
            onClick={() => handleIdiomaChange(l.code)}
          >
            {l.label}
          </LangPill>
        ))}
      </LangPills>
    </>
  );
};

export default CertificadosPage;
